import { toCanvas } from "html-to-image";
import jsPDF from "jspdf";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PDF_MARGIN_MM = 10;
const CSS_PX_PER_MM = 96 / 25.4;
const PRINT_PDF_PIXEL_RATIO = 2;
const PRINT_PDF_JPEG_QUALITY = 0.92;

const sanitizeFilename = (filename: string) =>
  filename
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 120);

const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

const PRINT_CAPTURE_STYLE_ID = "print-pdf-capture-style";
const PRINT_CAPTURE_SELECTOR = '[data-print-pdf-capture="true"]';

type PdfPageConfig = {
  orientation?: "p" | "l";
  marginMm: number;
};

type PageSegment = {
  sourceY: number;
  height: number;
};

type RepeatedTableHeader = PageSegment & {
  tableStartY: number;
  tableEndY: number;
};

export type PrintPdfPaginationLayout = {
  contentEndY: number;
  safeBreakpoints: number[];
  forcedBreakpoints: number[];
  tableHeaders: RepeatedTableHeader[];
};

export type PrintPdfPageSlice = {
  segments: PageSegment[];
  bodyStartY: number;
  bodyEndY: number;
};

const getSameOriginImageSrc = (url: string) => {
  try {
    const parsedUrl = new URL(url, window.location.href);
    if (
      parsedUrl.origin !== window.location.origin &&
      parsedUrl.pathname.startsWith("/images/")
    ) {
      return parsedUrl.pathname;
    }
  } catch {
    return undefined;
  }
};

export const isCrossOriginUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url, window.location.href);
    if (["data:", "blob:"].includes(parsedUrl.protocol)) {
      return false;
    }
    return parsedUrl.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const shouldRenderNode = (node: HTMLElement) => {
  if (node instanceof HTMLImageElement) {
    return !node.src || !isCrossOriginUrl(node.src);
  }

  return true;
};

const toMillimeters = (value: number, unit: string) => {
  switch (unit) {
    case "cm":
      return value * 10;
    case "in":
      return value * 25.4;
    case "px":
      return value / CSS_PX_PER_MM;
    case "mm":
    default:
      return value;
  }
};

export const getPrintPageConfigFromCss = (cssText: string): PdfPageConfig => {
  const config: PdfPageConfig = {
    marginMm: PDF_MARGIN_MM,
  };

  Array.from(cssText.matchAll(/@page[^{]*{([^}]*)}/gi)).forEach((match) => {
    const pageRule = match[1] ?? "";
    const sizeValue = pageRule.match(/size\s*:\s*([^;]+)/i)?.[1] ?? "";
    const marginValue = pageRule.match(
      /margin\s*:\s*([0-9]*\.?[0-9]+)\s*(mm|cm|in|px)?/i,
    );

    if (/\blandscape\b/i.test(sizeValue)) {
      config.orientation = "l";
    } else if (/\bportrait\b/i.test(sizeValue)) {
      config.orientation = "p";
    }

    if (marginValue) {
      config.marginMm = toMillimeters(
        Number.parseFloat(marginValue[1]),
        marginValue[2] ?? "mm",
      );
    }
  });

  return config;
};

const prefixSelectorForPrintCapture = (selector: string) => {
  const trimmed = selector.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (/^(html|body|:root)$/i.test(trimmed)) {
    return PRINT_CAPTURE_SELECTOR;
  }
  return `${PRINT_CAPTURE_SELECTOR} ${trimmed}`;
};

export const prefixPrintCssForCapture = (cssText: string) =>
  (cssText.match(/@media\s+print\s*{([\s\S]*)}/i)?.[1] ?? cssText)
    .replace(/@page[^{]*{[^}]*}/gi, "")
    .replace(/([^{}@]+){([^{}]+)}/g, (_match, selectorText, declaration) => {
      const selectors = String(selectorText)
        .split(",")
        .map(prefixSelectorForPrintCapture)
        .filter(Boolean)
        .join(", ");
      return selectors ? `${selectors} {${declaration}}` : "";
    });

const getRuleCssText = (rule: CSSRule, includeStyleRules: boolean): string => {
  if ("selectorText" in rule && "style" in rule) {
    if (!includeStyleRules) {
      return "";
    }
    const selectors = (rule as CSSStyleRule).selectorText
      .split(",")
      .map(prefixSelectorForPrintCapture)
      .filter(Boolean)
      .join(", ");
    return selectors
      ? `${selectors} { ${(rule as CSSStyleRule).style.cssText} }`
      : "";
  }

  if ("cssRules" in rule) {
    return getPrintCssTextFromRules(
      (rule as CSSMediaRule).cssRules,
      includeStyleRules,
    );
  }

  return "";
};

const getPrintCssTextFromRules = (
  rules: CSSRuleList,
  includeStyleRules = false,
) => {
  const cssText: string[] = [];

  Array.from(rules).forEach((rule) => {
    if (
      "conditionText" in rule &&
      /\bprint\b/i.test((rule as CSSMediaRule).conditionText)
    ) {
      cssText.push(
        getPrintCssTextFromRules((rule as CSSMediaRule).cssRules, true),
      );
      return;
    }

    if ("selectorText" in rule && "style" in rule) {
      cssText.push(getRuleCssText(rule, includeStyleRules));
    }
  });

  return cssText.filter(Boolean).join("\n");
};

const getPageCssTextFromRules = (rules: CSSRuleList) => {
  const cssText: string[] = [];

  Array.from(rules).forEach((rule) => {
    if (rule.cssText.trim().startsWith("@page")) {
      cssText.push(rule.cssText);
      return;
    }

    if (
      "conditionText" in rule &&
      /\bprint\b/i.test((rule as CSSMediaRule).conditionText)
    ) {
      cssText.push(getPageCssTextFromRules((rule as CSSMediaRule).cssRules));
    }
  });

  return cssText.filter(Boolean).join("\n");
};

const getDocumentPrintCssText = () => {
  const cssText: string[] = [];

  Array.from(document.styleSheets).forEach((styleSheet) => {
    try {
      cssText.push(getPrintCssTextFromRules(styleSheet.cssRules));
    } catch {
      // Cross-origin stylesheets cannot be inspected; local app styles are enough.
    }
  });

  return cssText.filter(Boolean).join("\n");
};

const getDocumentPageCssText = () => {
  const cssText: string[] = [];

  Array.from(document.styleSheets).forEach((styleSheet) => {
    try {
      cssText.push(getPageCssTextFromRules(styleSheet.cssRules));
    } catch {
      // Cross-origin stylesheets cannot be inspected.
    }
  });

  return cssText.filter(Boolean).join("\n");
};

const getInlineRawPrintCssText = (element: HTMLElement) =>
  Array.from(element.querySelectorAll("style"))
    .map((style) => style.textContent ?? "")
    .filter(Boolean)
    .join("\n");

const getInlinePrintCssText = (element: HTMLElement) =>
  Array.from(element.querySelectorAll("style"))
    .map((style) => style.textContent ?? "")
    .map(prefixPrintCssForCapture)
    .filter(Boolean)
    .join("\n");

export const getStaticPrintCaptureCss = () => `
  [data-print-pdf-capture="true"],
  [data-print-pdf-capture="true"] * {
    line-height: 1.45 !important;
    letter-spacing: normal !important;
    text-rendering: geometricPrecision !important;
  }

  [data-print-pdf-capture="true"] table,
  [data-print-pdf-capture="true"] th,
  [data-print-pdf-capture="true"] td {
    line-height: 1.35 !important;
  }

  [data-print-pdf-capture="true"] .print\\:hidden,
  [data-print-pdf-capture="true"] [data-print-hidden="true"] {
    display: none !important;
  }

  [data-print-pdf-capture="true"] .print\\:block {
    display: block !important;
  }

  [data-print-pdf-capture="true"] .print\\:inline {
    display: inline !important;
  }

  [data-print-pdf-capture="true"] .print\\:inline-block {
    display: inline-block !important;
  }

  [data-print-pdf-capture="true"] .print\\:flex {
    display: flex !important;
  }

  [data-print-pdf-capture="true"] .print\\:grid {
    display: grid !important;
  }

  [data-print-pdf-capture="true"] .print\\:table {
    display: table !important;
  }

  [data-print-pdf-capture="true"] .print\\:table-row {
    display: table-row !important;
  }

  [data-print-pdf-capture="true"] .print\\:table-cell {
    display: table-cell !important;
  }

  [data-print-pdf-capture="true"] .print\\:break-before-page,
  [data-print-pdf-capture="true"] .break-before-page,
  [data-print-pdf-capture="true"] .print-page-break-before {
    break-before: page !important;
    page-break-before: always !important;
  }

  [data-print-pdf-capture="true"] .print\\:p-0 {
    padding: 0 !important;
  }

  [data-print-pdf-capture="true"] .print\\:max-w-none {
    max-width: none !important;
  }
`;

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const waitForFonts = async () => {
  try {
    await document.fonts.ready;
  } catch {
    // Continue with available fonts if the browser cannot expose font loading.
  }
};

const waitForImages = async (element: HTMLElement) => {
  await Promise.all(
    Array.from(element.querySelectorAll("img")).map(async (image) => {
      if (!image.src || image.complete) {
        return;
      }

      try {
        await image.decode();
      } catch {
        // html-to-image will use imagePlaceholder/filter for images that fail.
      }
    }),
  );
};

const useSameOriginImageSources = async (element: HTMLElement) => {
  const changedImages: { image: HTMLImageElement; src: string }[] = [];

  await Promise.all(
    Array.from(element.querySelectorAll("img")).map(async (image) => {
      const localSrc = getSameOriginImageSrc(image.src);
      if (localSrc) {
        changedImages.push({ image, src: image.src });
        image.src = localSrc;
        return;
      }

      if (!image.src || !isCrossOriginUrl(image.src)) {
        return;
      }

      try {
        const response = await fetch(image.src, {
          credentials: "omit",
          mode: "cors",
        });
        if (!response.ok) {
          return;
        }
        const dataUrl = await blobToDataUrl(await response.blob());
        changedImages.push({ image, src: image.src });
        image.src = dataUrl;
      } catch {
        // The html-to-image placeholder/filter handles remote images blocked by CORS.
      }
    }),
  );

  await waitForImages(element);

  return () => {
    changedImages.forEach(({ image, src }) => {
      image.src = src;
    });
  };
};

const usePrintCaptureStyles = (element: HTMLElement) => {
  const previousAttribute = element.getAttribute("data-print-pdf-capture");
  element.setAttribute("data-print-pdf-capture", "true");
  const printCssText = [
    getDocumentPrintCssText(),
    getInlinePrintCssText(element),
  ].join("\n");
  const pageConfig = getPrintPageConfigFromCss(
    [getDocumentPageCssText(), getInlineRawPrintCssText(element)].join("\n"),
  );

  const style = document.createElement("style");
  style.id = PRINT_CAPTURE_STYLE_ID;
  style.textContent = `
    ${printCssText}
    ${getStaticPrintCaptureCss()}
  `;
  document.head.appendChild(style);

  return {
    pageConfig,
    restore: () => {
      style.remove();
      if (previousAttribute === null) {
        element.removeAttribute("data-print-pdf-capture");
      } else {
        element.setAttribute("data-print-pdf-capture", previousAttribute);
      }
    },
  };
};

const usePrintLayout = (element: HTMLElement, pageConfig: PdfPageConfig) => {
  const previousWidth = element.style.width;
  const previousMaxWidth = element.style.maxWidth;
  const isLandscape = pageConfig.orientation === "l";
  const pageWidth = isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM;
  const contentWidthPx = Math.round(
    (pageWidth - pageConfig.marginMm * 2) * CSS_PX_PER_MM,
  );

  element.style.width = `${contentWidthPx}px`;
  element.style.maxWidth = "none";

  return () => {
    element.style.width = previousWidth;
    element.style.maxWidth = previousMaxWidth;
  };
};

async function renderPrintElementToCanvas(element: HTMLElement) {
  await waitForFonts();
  const restoreImageSources = await useSameOriginImageSources(element);
  const { pageConfig, restore: restorePrintCaptureStyles } =
    usePrintCaptureStyles(element);
  const restorePrintLayout = usePrintLayout(element, pageConfig);
  try {
    const canvas = await toCanvas(element, {
      backgroundColor: "#ffffff",
      filter: shouldRenderNode,
      pixelRatio: PRINT_PDF_PIXEL_RATIO,
      cacheBust: false,
      imagePlaceholder: TRANSPARENT_PIXEL,
    });
    return {
      canvas,
      pageConfig,
      paginationLayout: getPrintPdfPaginationLayout(element, canvas),
    };
  } catch (_error) {
    const canvas = await toCanvas(element, {
      backgroundColor: "#ffffff",
      filter: shouldRenderNode,
      pixelRatio: PRINT_PDF_PIXEL_RATIO,
      cacheBust: false,
      imagePlaceholder: TRANSPARENT_PIXEL,
      skipFonts: true,
    });
    return {
      canvas,
      pageConfig,
      paginationLayout: getPrintPdfPaginationLayout(element, canvas),
    };
  } finally {
    restorePrintLayout();
    restorePrintCaptureStyles();
    restoreImageSources();
  }
}

const getCanvasY = (
  elementRect: DOMRect,
  canvas: HTMLCanvasElement,
  child: Element,
) => {
  const childRect = child.getBoundingClientRect();
  if (!elementRect.height) {
    return 0;
  }
  return Math.round(
    ((childRect.top - elementRect.top) / elementRect.height) * canvas.height,
  );
};

const getCanvasHeight = (
  elementRect: DOMRect,
  canvas: HTMLCanvasElement,
  child: Element,
) => {
  const childRect = child.getBoundingClientRect();
  if (!elementRect.height) {
    return 0;
  }
  return Math.round((childRect.height / elementRect.height) * canvas.height);
};

const getRepeatedTableHeaders = (
  element: HTMLElement,
  canvas: HTMLCanvasElement,
): RepeatedTableHeader[] => {
  const elementRect = element.getBoundingClientRect();
  return Array.from(element.querySelectorAll(".mar-thead, thead"))
    .map((header) => {
      const table = header.closest("table");
      if (!table) {
        return undefined;
      }

      const sourceY = Math.max(0, getCanvasY(elementRect, canvas, header));
      const height = Math.min(
        canvas.height - sourceY,
        getCanvasHeight(elementRect, canvas, header),
      );
      const tableStartY = Math.max(0, getCanvasY(elementRect, canvas, table));
      const tableEndY = Math.min(
        canvas.height,
        tableStartY + getCanvasHeight(elementRect, canvas, table),
      );

      if (height <= 0 || tableEndY <= tableStartY) {
        return undefined;
      }

      return { sourceY, height, tableStartY, tableEndY };
    })
    .filter((header): header is RepeatedTableHeader => Boolean(header));
};

const getSafeBreakpoints = (
  element: HTMLElement,
  canvas: HTMLCanvasElement,
): number[] => {
  const elementRect = element.getBoundingClientRect();
  const breakpoints = Array.from(
    element.querySelectorAll("tr, .mar-row, [data-print-row]"),
  )
    .map(
      (row) =>
        getCanvasY(elementRect, canvas, row) +
        getCanvasHeight(elementRect, canvas, row),
    )
    .filter((offset) => offset > 0 && offset < canvas.height)
    .sort((a, b) => a - b);

  return Array.from(new Set(breakpoints));
};

const getForcedBreakpoints = (
  element: HTMLElement,
  canvas: HTMLCanvasElement,
) => {
  const elementRect = element.getBoundingClientRect();
  const breakpoints = Array.from(
    element.querySelectorAll(
      ".print\\:break-before-page, .break-before-page, .print-page-break-before, [data-print-page-break-before]",
    ),
  )
    .map((node) => getCanvasY(elementRect, canvas, node))
    .filter((offset) => offset > 0 && offset < canvas.height)
    .sort((a, b) => a - b);

  return Array.from(new Set(breakpoints));
};

const getContentEndY = (element: HTMLElement, canvas: HTMLCanvasElement) => {
  const elementRect = element.getBoundingClientRect();
  const descendantEnds = Array.from(element.querySelectorAll("*"))
    .map((node) => {
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return 0;
      }

      return (
        getCanvasY(elementRect, canvas, node) +
        getCanvasHeight(elementRect, canvas, node)
      );
    })
    .filter((offset) => offset > 0 && offset <= canvas.height);

  return Math.max(1, ...descendantEnds);
};

export const getPrintPdfPaginationLayout = (
  element: HTMLElement,
  canvas: HTMLCanvasElement,
): PrintPdfPaginationLayout => ({
  contentEndY: getContentEndY(element, canvas),
  safeBreakpoints: getSafeBreakpoints(element, canvas),
  forcedBreakpoints: getForcedBreakpoints(element, canvas),
  tableHeaders: getRepeatedTableHeaders(element, canvas),
});

const getRepeatedHeaderForPage = (
  tableHeaders: RepeatedTableHeader[],
  bodyStartY: number,
) =>
  tableHeaders.find(
    (header) =>
      bodyStartY >= header.sourceY + header.height &&
      bodyStartY >= header.tableStartY &&
      bodyStartY < header.tableEndY,
  );

const findSafePageEnd = (
  breakpoints: number[],
  startY: number,
  targetEndY: number,
) => {
  const safeEnd = breakpoints
    .filter((breakpoint) => breakpoint > startY && breakpoint <= targetEndY)
    .at(-1);

  return safeEnd ?? targetEndY;
};

export const getPrintPdfPageSlices = (
  canvas: HTMLCanvasElement,
  pageContentHeightPx: number,
  paginationLayout: PrintPdfPaginationLayout,
): PrintPdfPageSlice[] => {
  const slices: PrintPdfPageSlice[] = [];
  let bodyStartY = 0;

  while (bodyStartY < paginationLayout.contentEndY) {
    const headerSegment =
      slices.length > 0
        ? getRepeatedHeaderForPage(paginationLayout.tableHeaders, bodyStartY)
        : undefined;
    const repeatedHeaderHeight = headerSegment?.height ?? 0;
    const availableBodyHeight = Math.max(
      1,
      pageContentHeightPx - repeatedHeaderHeight,
    );
    const targetEndY = Math.min(
      paginationLayout.contentEndY,
      bodyStartY + availableBodyHeight,
    );
    const forcedEndY = paginationLayout.forcedBreakpoints.find(
      (breakpoint) => breakpoint > bodyStartY && breakpoint <= targetEndY,
    );
    const bodyEndY =
      forcedEndY ??
      (targetEndY === paginationLayout.contentEndY
        ? targetEndY
        : findSafePageEnd(
            paginationLayout.safeBreakpoints,
            bodyStartY,
            targetEndY,
          ));
    const bodyHeight = Math.max(1, bodyEndY - bodyStartY);
    const segments: PageSegment[] = [];

    if (slices.length > 0 && headerSegment) {
      segments.push(headerSegment);
    }

    segments.push({
      sourceY: bodyStartY,
      height: bodyHeight,
    });

    slices.push({ segments, bodyStartY, bodyEndY });
    bodyStartY = bodyEndY;
  }

  return slices;
};

const createCanvasForSlice = (
  canvas: HTMLCanvasElement,
  slice: PrintPdfPageSlice,
) => {
  const pageCanvas = document.createElement("canvas");
  pageCanvas.width = canvas.width;
  pageCanvas.height = slice.segments.reduce(
    (height, segment) => height + segment.height,
    0,
  );

  const context = pageCanvas.getContext("2d");
  if (!context) {
    throw new Error("Could not create a canvas for a PDF page");
  }

  let targetY = 0;
  slice.segments.forEach((segment) => {
    context.drawImage(
      canvas,
      0,
      segment.sourceY,
      canvas.width,
      segment.height,
      0,
      targetY,
      canvas.width,
      segment.height,
    );
    targetY += segment.height;
  });

  return pageCanvas;
};

export const buildPrintPdfFile = (
  canvas: HTMLCanvasElement,
  pageConfig: PdfPageConfig,
  paginationLayout: PrintPdfPaginationLayout,
  documentTitle: string,
) => {
  const orientation =
    pageConfig.orientation ?? (canvas.width > canvas.height ? "l" : "p");
  const isLandscape = orientation === "l";
  const marginMm = pageConfig.marginMm;
  const pdf = new jsPDF(orientation, "mm", "a4");
  const pageWidth = (isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM) - marginMm * 2;
  const pageHeight = (isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM) - marginMm * 2;
  const pageContentHeightPx = Math.floor(
    (pageHeight / pageWidth) * canvas.width,
  );
  const pageSlices = getPrintPdfPageSlices(
    canvas,
    pageContentHeightPx,
    paginationLayout,
  );

  pageSlices.forEach((slice, index) => {
    if (index > 0) {
      pdf.addPage("a4", orientation);
    }

    const pageCanvas = createCanvasForSlice(canvas, slice);
    const imageHeight = Math.min(
      pageHeight,
      (pageCanvas.height * pageWidth) / pageCanvas.width,
    );
    pdf.addImage(
      pageCanvas.toDataURL("image/jpeg", PRINT_PDF_JPEG_QUALITY),
      "JPEG",
      marginMm,
      marginMm,
      pageWidth,
      imageHeight,
    );
  });

  const filename = `${sanitizeFilename(documentTitle) || "document"}.pdf`;
  return new File([pdf.output("blob")], filename, {
    type: "application/pdf",
  });
};

export async function generatePrintElementPdf(
  elementId: string,
  documentTitle: string,
) {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error("Printable content was not found");
  }

  const { canvas, pageConfig, paginationLayout } =
    await renderPrintElementToCanvas(element);

  return buildPrintPdfFile(canvas, pageConfig, paginationLayout, documentTitle);
}
